import { forwardRef } from "react";
import { Layers3, Search } from "lucide-react";

interface ShellSurfaceControlsProps {
  chatOn: boolean;
  showOn: boolean;
  workOn: boolean;
  onChatToggle: () => void;
  onShowToggle: () => void;
  onWorkToggle: () => void;
  onSearch?: () => void;
  includeSearch?: boolean;
  includeWork?: boolean;
  /**
   * Mobile hides the Chat toggle: Chat is the default/home surface, not a
   * peer destination, and the composer sheet is the chat entry point there.
   */
  includeChat?: boolean;
  className?: string;
}

interface ShellWorkControlProps {
  workOn: boolean;
  onWorkToggle: () => void;
  className?: string;
  transferWhenOpen?: boolean;
}

interface ShellActivityControlProps {
  open: boolean;
  onToggle: () => void;
  hasAttention?: boolean;
  className?: string;
}

export const ShellActivityControl = forwardRef<
  HTMLButtonElement,
  ShellActivityControlProps
>(function ShellActivityControl({
  open,
  onToggle,
  hasAttention = false,
  className = "",
}, ref) {
  return (
    <button
      ref={ref}
      type="button"
      data-control="activity-center"
      aria-label={`${open ? "Close" : "Open"} Activity Center`}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-controls="bearing-activity-center"
      className={`${className} ${open ? "active" : ""}`.trim()}
      onClick={onToggle}
      title="Activity Center"
    >
      <Layers3 size={18} strokeWidth={1.8} aria-hidden="true" />
      {hasAttention && (
        <span className="bearing-shell-activity-dot" aria-hidden="true" />
      )}
    </button>
  );
});

export const ShellWorkControl = forwardRef<
  HTMLButtonElement,
  ShellWorkControlProps
>(function ShellWorkControl({
  workOn,
  onWorkToggle,
  className = "",
  transferWhenOpen = false,
}, ref) {
  const transferred = transferWhenOpen && workOn;

  return (
    <button
      ref={ref}
      type="button"
      data-surface="work"
      aria-pressed={workOn}
      aria-expanded={workOn}
      aria-controls="orca-work-pane"
      aria-label="Toggle Work"
      aria-hidden={transferred || undefined}
      tabIndex={transferred ? -1 : undefined}
      className={`${className} ${workOn ? "active" : ""} ${transferred ? "is-transferred" : ""}`.trim()}
      onClick={onWorkToggle}
      title={`Turn Work ${workOn ? "off" : "on"}`}
    >
      Work
    </button>
  );
});

export function ShellSurfaceControls({
  chatOn,
  showOn,
  workOn,
  onChatToggle,
  onShowToggle,
  onWorkToggle,
  onSearch,
  includeSearch = false,
  includeWork = true,
  includeChat = true,
  className = "",
}: ShellSurfaceControlsProps) {
  return (
    <div
      className={`orca-shell-surface-controls ${className}`.trim()}
      role="group"
      aria-label="Workspace surfaces"
    >
      {includeSearch && onSearch && (
        <button
          type="button"
          className="orca-shell-search-control"
          data-control="global-search"
          aria-label="Open global search"
          aria-haspopup="dialog"
          aria-controls="globalSearchBack"
          onClick={onSearch}
          title="Global Search"
        >
          <Search size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
      {includeChat && (
        <button
          type="button"
          data-surface="chat"
          aria-pressed={chatOn}
          aria-label="Toggle Chat"
          className={chatOn ? "active" : ""}
          onClick={onChatToggle}
          title={`Turn Chat ${chatOn ? "off" : "on"}`}
        >
          Chat
        </button>
      )}
      <button
        type="button"
        data-surface="show"
        aria-pressed={showOn}
        aria-label="Toggle Show"
        className={showOn ? "active" : ""}
        onClick={onShowToggle}
        title={`Turn Show ${showOn ? "off" : "on"}`}
      >
        Show
      </button>
      {includeWork && (
        <ShellWorkControl
          workOn={workOn}
          onWorkToggle={onWorkToggle}
        />
      )}
    </div>
  );
}

export default ShellSurfaceControls;
